import { Directive, ElementRef, inject } from "@angular/core";
import { Tooltip } from "primeng/tooltip";

/**
 * Whether the element's content is clipped.
 *
 * PrimeNG's own check (`offsetWidth < scrollWidth`) compares whole pixels and
 * counts the border into `offsetWidth`, so text that overflows by up to ~1.5px
 * shows an ellipsis but no tooltip. The range measures the inline content at
 * sub-pixel precision; the whole-pixel check still covers block children,
 * whose overflowing descendants the range does not see.
 */
export function isTruncated(el: HTMLElement): boolean {
  if (el.offsetWidth < el.scrollWidth || el.offsetHeight < el.scrollHeight) {
    return true;
  }

  const range = document.createRange();
  range.selectNodeContents(el);
  const style = getComputedStyle(el);
  const contentWidth =
    el.getBoundingClientRect().width -
    parseFloat(style.paddingLeft) -
    parseFloat(style.paddingRight) -
    parseFloat(style.borderLeftWidth) -
    parseFloat(style.borderRightWidth);

  return range.getBoundingClientRect().width > contentWidth;
}

// Put it next to `showOnEllipsis` on a `pTooltip` host.
@Directive({
  selector: "[appTooltipOnEllipsis]",
})
export class TooltipOnEllipsisDirective {
  constructor() {
    const el: HTMLElement = inject(ElementRef).nativeElement;

    inject(Tooltip).hasEllipsis = () => isTruncated(el);
  }
}
